/**
 * Translate `BaseProps` animation props into `data-*` attributes and inline
 * CSS variables that the component's stylesheet keys on.
 *
 * Stage 1 ships CSS-keyframe-driven entrances/loops/hovers. Stage 1.5 (motion)
 * augments this with the JS engine for spring physics, scroll-link, etc.
 * Both layers read the same `data-rr-entrance`, `data-rr-hover`, … attrs so
 * components don't need to change when motion is upgraded.
 */

import type { CSSProperties } from 'react';
import type {
  EntrancePreset,
  EntranceConfig,
  HoverPreset,
  HoverConfig,
  LoopPreset,
  LoopConfig,
  FocusPreset,
  FocusConfig,
  ExitPreset,
  ExitConfig,
  EasingToken,
  BaseProps
} from './types.js';

export interface AnimationAttrs {
  attrs: Record<string, string | undefined>;
  style: CSSProperties;
}

function normalizeEntrance(p: BaseProps['entrance']): { preset?: EntrancePreset; config?: EntranceConfig } {
  if (p === false || p === undefined) return {};
  if (typeof p === 'string') return { preset: p };
  return { preset: p.preset, config: p };
}

function normalizeHover(p: BaseProps['hover']): { preset?: HoverPreset; config?: HoverConfig } {
  if (p === false || p === undefined) return {};
  if (typeof p === 'string') return { preset: p };
  return { preset: p.preset, config: p };
}

function normalizeLoop(p: BaseProps['loop']): { preset?: LoopPreset; config?: LoopConfig } {
  if (p === false || p === undefined) return {};
  if (typeof p === 'string') return { preset: p };
  return { preset: p.preset, config: p };
}

function normalizeFocus(p: BaseProps['focusAnim']): { preset?: FocusPreset; config?: FocusConfig } {
  if (p === false || p === undefined) return {};
  if (typeof p === 'string') return { preset: p };
  return { preset: p.preset, config: p };
}

function normalizeExit(p: BaseProps['exit']): { preset?: ExitPreset; config?: ExitConfig } {
  if (p === false || p === undefined) return {};
  if (typeof p === 'string') return { preset: p };
  return { preset: p.preset, config: p };
}

function easingToCss(easing: EasingToken | string | undefined): string | undefined {
  if (!easing) return undefined;
  if (['linear', 'ease', 'easeIn', 'easeOut', 'easeInOut', 'bounce', 'sharp'].includes(easing)) {
    return `var(--rr-easing-${easing})`;
  }
  return easing;
}

/**
 * Build the data attributes and instance CSS vars for a component's animation
 * props. Components spread the `attrs` onto the root element and merge the
 * `style` with their own.
 */
export function buildAnimationAttrs(props: BaseProps): AnimationAttrs {
  const entrance = normalizeEntrance(props.entrance);
  const hover = normalizeHover(props.hover);
  const loop = normalizeLoop(props.loop);
  const focus = normalizeFocus(props.focusAnim);
  const exit = normalizeExit(props.exit);

  const attrs: Record<string, string | undefined> = {};
  // CSS custom properties aren't in the React CSSProperties type: cast to
  // `Record<string, string>` for the var entries, then merge into the
  // final CSSProperties shape on return.
  const styleVars: Record<string, string> = {};

  if (entrance.preset) {
    attrs['data-rr-entrance'] = entrance.preset;
    if (entrance.config?.trigger) attrs['data-rr-entrance-trigger'] = entrance.config.trigger;
  }
  if (hover.preset) attrs['data-rr-hover'] = hover.preset;
  if (loop.preset) attrs['data-rr-loop'] = loop.preset;
  if (focus.preset) attrs['data-rr-focus'] = focus.preset;
  if (exit.preset) attrs['data-rr-exit'] = exit.preset;

  if (props.staggerIndex !== undefined) {
    styleVars['--rr-stagger-index'] = String(props.staggerIndex);
  }

  const delay = props.entranceDelay ?? entrance.config?.delay;
  if (delay !== undefined) styleVars['--rr-entrance-delay'] = `${delay}ms`;
  const dur = props.entranceDuration ?? entrance.config?.duration;
  if (dur !== undefined) styleVars['--rr-entrance-duration'] = `${dur}ms`;
  const ease = easingToCss(props.entranceEasing ?? entrance.config?.easing);
  if (ease) styleVars['--rr-entrance-easing'] = ease;
  if (loop.config?.duration) styleVars['--rr-loop-duration'] = `${loop.config.duration}ms`;
  const loopEase = easingToCss(loop.config?.easing);
  if (loopEase) styleVars['--rr-loop-easing'] = loopEase;
  if (focus.config?.width) styleVars['--rr-focus-ring-width'] = `${focus.config.width}px`;
  if (focus.config?.color) styleVars['--rr-focus-ring-color'] = focus.config.color;

  const style = styleVars as unknown as CSSProperties;

  // Per-component reduced-motion override.
  if (props.reducedMotion) {
    attrs['data-rr-reduce-motion'] = props.reducedMotion;
  }

  return { attrs, style };
}
